import { useEffect, useState, useMemo } from "react";
import { api } from "../api";
import { formatVND } from "../utils";
import { GlassPanel, KpiCard } from "../components";

// TODO: COGS chưa tính chính xác — cần endpoint /api/finance/cogs

type Period = "month" | "quarter" | "year" | "custom";

const EXPENSE_CATEGORIES: Record<string, string> = {
  mat_bang: "Mặt bằng",
  dien: "Điện",
  nuoc: "Nước",
  luong: "Lương",
  ship: "Vận chuyển",
  khac: "Khác",
};

function getRange(period: Period): { from: string; to: string } {
  const now = new Date();
  const to = now.toISOString().slice(0, 10);
  let from: string;

  switch (period) {
    case "month": {
      const d = new Date(now.getFullYear(), now.getMonth(), 1);
      from = d.toISOString().slice(0, 10);
      break;
    }
    case "quarter": {
      const qMonth = Math.floor(now.getMonth() / 3) * 3;
      const d = new Date(now.getFullYear(), qMonth, 1);
      from = d.toISOString().slice(0, 10);
      break;
    }
    case "year": {
      from = `${now.getFullYear()}-01-01`;
      break;
    }
    default:
      from = to;
  }
  return { from, to };
}

function pct(part: number, total: number): string {
  if (!total) return "0%";
  return `${((part / total) * 100).toFixed(1)}%`;
}

/** Trang Lãi / Lỗ (P&L Report) */
export default function PnLReportPage() {
  const [period, setPeriod] = useState<Period>("month");
  const [customFrom, setCustomFrom] = useState(() => {
    const d = new Date(); d.setDate(1);
    return d.toISOString().slice(0, 10);
  });
  const [customTo, setCustomTo] = useState(new Date().toISOString().slice(0, 10));

  const [revenue, setRevenue] = useState(0);
  const [actualCogs, setActualCogs] = useState(0);
  const [cogsOverride, setCogsOverride] = useState<string>("");
  const [expenses, setExpenses] = useState<{ category: string; amount: number }[]>([]);
  const [loading, setLoading] = useState(true);

  const range = useMemo(() => {
    if (period === "custom") return { from: customFrom, to: customTo };
    return getRange(period);
  }, [period, customFrom, customTo]);

  useEffect(() => {
    let alive = true;
    setLoading(true);

    Promise.all([
      api.summary(range.from, range.to),
      api.expenses.list({ from: range.from, to: range.to, limit: 500 }),
      api.salesHistoryStats(range.from, range.to),
    ])
      .then(([summary, expData, salesStats]) => {
        if (!alive) return;
        setRevenue(summary.totalSales || 0);
        setActualCogs(salesStats?.total_cost || 0);
        // Aggregate expenses by category
        const byCategory: Record<string, number> = {};
        (expData.expenses || []).forEach((e: { category: string; amount: number }) => {
          byCategory[e.category] = (byCategory[e.category] || 0) + e.amount;
        });
        setExpenses(
          Object.entries(byCategory).map(([category, amount]) => ({ category, amount }))
        );
      })
      .catch(() => {})
      .finally(() => alive && setLoading(false));

    return () => { alive = false; };
  }, [range.from, range.to]);

  // COGS: ưu tiên user override, fallback dùng actual từ sheet_sales
  const cogs = cogsOverride !== "" ? Number(cogsOverride) || 0 : actualCogs;
  const totalExpenses = expenses.reduce((s, e) => s + e.amount, 0);
  const grossProfit = revenue - cogs;
  const netProfit = grossProfit - totalExpenses;
  const grossMargin = pct(grossProfit, revenue);
  const netMargin = pct(netProfit, revenue);

  const sortedExpenses = [...expenses].sort((a, b) => b.amount - a.amount);

  const TABS: { key: Period; label: string }[] = [
    { key: "month", label: "Tháng này" },
    { key: "quarter", label: "Quý này" },
    { key: "year", label: "Năm này" },
    { key: "custom", label: "Tùy chọn" },
  ];

  return (
    <>
      {/* Period tabs */}
      <div className="tab-bar" style={{ marginBottom: 16 }}>
        {TABS.map((t) => (
          <button
            key={t.key}
            className={`tab-btn ${period === t.key ? "active" : ""}`}
            onClick={() => setPeriod(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Custom date range */}
      {period === "custom" && (
        <div className="daterange" style={{ marginBottom: 16 }}>
          <div className="field">
            <label>Từ</label>
            <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
          </div>
          <div className="field">
            <label>Đến</label>
            <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
          </div>
        </div>
      )}

      {/* KPI summary */}
      <div className="kpi-grid">
        <KpiCard label="Doanh thu" value={formatVND(revenue)} dotColor="ok" note={`${range.from} → ${range.to}`} />
        <KpiCard label="Lãi gộp" value={formatVND(grossProfit)} dotColor={grossProfit >= 0 ? "ok" : "bad"} note={`Margin ${grossMargin}`} />
        <KpiCard label="Chi phí" value={formatVND(totalExpenses)} dotColor="warn" note={`${expenses.length} nhóm`} />
        <KpiCard label="Lãi ròng" value={formatVND(netProfit)} dotColor={netProfit >= 0 ? "ok" : "bad"} note={`Margin ${netMargin}`} />
      </div>

      {loading ? (
        <div className="loading">Đang tải dữ liệu…</div>
      ) : (
        <>
          {/* P&L Statement */}
          <GlassPanel title="Báo cáo Lãi / Lỗ" badge={period === "custom" ? `${customFrom} → ${customTo}` : TABS.find(t => t.key === period)?.label}>
            <div className="pnl-statement">
              <div className="pnl-row">
                <span className="pnl-label">Doanh thu</span>
                <span className="pnl-value" style={{ color: "var(--accent-emerald)" }}>{formatVND(revenue)}</span>
              </div>

              <div className="pnl-row pnl-row-indent">
                <span className="pnl-label">
                  (-) Giá vốn (COGS)
                  <input
                    type="number"
                    className="pnl-cogs-input"
                    placeholder={actualCogs > 0 ? `Tự động ${formatVND(actualCogs)}` : "Nhập ước tính"}
                    value={cogsOverride}
                    onChange={(e) => setCogsOverride(e.target.value)}
                    title="Mặc định lấy từ sheet_sales.cost — nhập số để override"
                  />
                </span>
                <span className="pnl-value" style={{ color: "var(--accent-red)" }}>
                  ({formatVND(cogs)})
                </span>
              </div>

              <div className="pnl-divider" />

              <div className="pnl-row pnl-row-bold">
                <span className="pnl-label">Lãi gộp</span>
                <span className="pnl-value" style={{ color: grossProfit >= 0 ? "var(--accent-emerald)" : "var(--accent-red)" }}>
                  {formatVND(grossProfit)}
                  <small className="pnl-pct">{grossMargin}</small>
                </span>
              </div>

              <div className="pnl-row pnl-row-indent">
                <span className="pnl-label">(-) Chi phí vận hành</span>
                <span className="pnl-value" style={{ color: "var(--accent-red)" }}>
                  ({formatVND(totalExpenses)})
                </span>
              </div>

              <div className="pnl-divider" />

              <div className="pnl-row pnl-row-bold pnl-row-final">
                <span className="pnl-label">Lãi ròng</span>
                <span className="pnl-value" style={{ color: netProfit >= 0 ? "var(--accent-emerald)" : "var(--accent-red)" }}>
                  {formatVND(netProfit)}
                  <small className="pnl-pct">{netMargin}</small>
                </span>
              </div>
            </div>
          </GlassPanel>

          {/* Expense breakdown table */}
          {sortedExpenses.length > 0 && (
            <GlassPanel title="Chi phí chi tiết" badge={`${sortedExpenses.length} nhóm`}>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Nhóm chi phí</th>
                      <th style={{ textAlign: "right" }}>Số tiền</th>
                      <th style={{ textAlign: "right" }}>% tổng</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedExpenses.map((e) => (
                      <tr key={e.category}>
                        <td>{EXPENSE_CATEGORIES[e.category] || e.category}</td>
                        <td style={{ textAlign: "right", fontWeight: 700, color: "var(--accent-red)" }}>
                          {formatVND(e.amount)}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          {pct(e.amount, totalExpenses)}
                        </td>
                      </tr>
                    ))}
                    <tr style={{ fontWeight: 700, borderTop: "1px solid var(--border)" }}>
                      <td>Tổng</td>
                      <td style={{ textAlign: "right", color: "var(--accent-red)" }}>{formatVND(totalExpenses)}</td>
                      <td style={{ textAlign: "right" }}>100%</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </GlassPanel>
          )}

          {/* Visual: progress bars */}
          <GlassPanel title="Tỷ lệ phân bổ doanh thu">
            <div className="pnl-bars">
              {[
                { label: "Giá vốn (COGS)", value: cogs, color: "var(--accent-yellow)" },
                { label: "Chi phí vận hành", value: totalExpenses, color: "var(--accent-red)" },
                { label: "Lãi ròng", value: Math.max(netProfit, 0), color: "var(--accent-emerald)" },
              ].map((item) => (
                <div className="cat-row" key={item.label}>
                  <div className="cat-row-head">
                    <span className="cat-name">{item.label}</span>
                    <span className="cat-amount">{formatVND(item.value)} ({pct(item.value, revenue)})</span>
                  </div>
                  <div className="cat-bar-track">
                    <div
                      className="cat-bar-fill"
                      style={{
                        width: `${revenue > 0 ? Math.max(1, (item.value / revenue) * 100) : 0}%`,
                        background: item.color,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </GlassPanel>
        </>
      )}
    </>
  );
}
